import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import PropertyListing from './PropertyListing';
import AgodaBookingModal from './AgodaBookingModal';
import { initializePredefinedCities } from '../../utils/cityConfigUtils';

const AgodaPage = () => {
  const { currentUser } = useAuth();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedTripPlan, setSelectedTripPlan] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeIntegration();
  }, []);

  const initializeIntegration = async () => {
    if (!currentUser?.uid) return;
    
    try {
      const result = await initializePredefinedCities(currentUser.uid);
      if (result.success) {
        console.log('Agoda integration initialized successfully');
        setInitialized(true);
      } else {
        console.error('Failed to initialize Agoda integration:', result.error);
      }
    } catch (error) {
      console.error('Error initializing Agoda integration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setShowBookingModal(true);
  };

  const handleBookingSuccess = (booking) => {
    console.log('Booking successful:', booking);
    setShowBookingModal(false);
    setSelectedProperty(null);
    setSelectedTripPlan(null);
    
    // Show success message
    alert(`การจองสำเร็จ! Booking ID: ${booking.bookingId}\nCommission: ฿${booking.commissionAmount.toLocaleString()}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">จองที่พักและร้านอาหาร</h1>
              <p className="mt-2 text-gray-600">
                ค้นหาและจองโรงแรม ร้านอาหาร และสถานที่ท่องเที่ยวผ่าน Agoda
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-gray-500">Commission Rate</div>
                <div className="text-lg font-semibold text-green-600">
                  {currentUser?.role === 'customer' ? '5%' : 
                   currentUser?.role === 'momo' ? '8%' : 
                   currentUser?.role === 'girl' ? '3%' : 
                   currentUser?.role === 'admin' ? '10%' : '5%'}
                </div>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Property Categories */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Hotels */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">โรงแรม & รีสอร์ท</h3>
                  <p className="text-sm text-gray-600">ที่พักคุณภาพสูง</p>
                </div>
              </div>
              <PropertyListing
                propertyType="hotels"
                limit={6}
                showFilters={false}
                onPropertySelect={handlePropertySelect}
                className="space-y-4"
              />
            </div>

            {/* Restaurants */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 p-3 rounded-lg mr-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">ร้านอาหาร & คาเฟ่</h3>
                  <p className="text-sm text-gray-600">ประสบการณ์การรับประทานอาหาร</p>
                </div>
              </div>
              <PropertyListing
                propertyType="restaurants"
                limit={6}
                showFilters={false}
                onPropertySelect={handlePropertySelect}
                className="space-y-4"
              />
            </div>

            {/* Attractions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <div className="bg-purple-100 p-3 rounded-lg mr-4">
                  <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">สถานที่ท่องเที่ยว</h3>
                  <p className="text-sm text-gray-600">กิจกรรมและสถานที่ที่น่าสนใจ</p>
                </div>
              </div>
              <PropertyListing
                propertyType="attractions"
                limit={6}
                showFilters={false}
                onPropertySelect={handlePropertySelect}
                className="space-y-4"
              />
            </div>
          </div>
        </div>

        {/* Advanced Search */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">ค้นหาขั้นสูง</h2>
            <div className="text-sm text-gray-500">
              Powered by Agoda
            </div>
          </div>
          
          <PropertyListing
            propertyType="all"
            limit={20}
            showFilters={true}
            onPropertySelect={handlePropertySelect}
          />
        </div>

        {/* Commission Info */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                เกี่ยวกับ Commission
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  คุณจะได้รับ commission สำหรับการจองที่สำเร็จผ่านระบบของเรา
                  อัตรา commission ขึ้นอยู่กับ role ของคุณ:
                </p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Customer: 5%</li>
                  <li>Momo: 8%</li>
                  <li>Girl: 3%</li>
                  <li>Admin: 10%</li>
                </ul>
                <p className="mt-2">
                  Commission จะถูกคำนวณและติดตามในระบบอัตโนมัติ
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {selectedProperty && (
        <AgodaBookingModal
          property={selectedProperty}
          tripPlan={selectedTripPlan}
          isOpen={showBookingModal}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedProperty(null);
            setSelectedTripPlan(null);
          }}
          onBookingSuccess={handleBookingSuccess}
        />
      )}
    </div>
  );
};

export default AgodaPage;
